/**
 * HiLSテスト管理ページ
 * Hardware-in-the-Loop Simulation OTAテスト結果表示
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { buildApi } from '../services/api';
import { Icon } from '../components/icons';
import { Button, Card, Alert } from '../components/ui';

// キャッシュTTL（ミリ秒）
const CACHE_TTL = 30000; // 30秒

// --- OTAテスト項目定義（targetType別） ---
// ota-agent モード（従来）
const TEST_ITEMS_OTA_AGENT = [
  { id: 'otaDelivery', name: 'OTA配信', description: 'CM5へのOTAジョブ配信完了' },
  { id: 'versionReport', name: 'バージョン通知', description: 'Device Shadowへのバージョン報告' },
  { id: 'reboot', name: '再起動確認', description: 'CM5再起動と完了通知' },
  { id: 'mqttMessage', name: 'MQTT疎通', description: 'MQTTメッセージ送受信確認' },
];

// RAUC モード（.raucbバンドル）
const TEST_ITEMS_RAUC = [
  { id: 'otaDelivery', name: 'OTA配信', description: 'CM5へのOTAジョブ配信完了' },
  { id: 'raucInstall', name: 'RAUCインストール', description: 'rauc installコマンドの実行結果' },
  { id: 'bootSlot', name: 'ブートスロット', description: '新パーティション（A/B）での起動確認' },
  { id: 'osVersion', name: 'OSバージョン', description: '期待バージョンとの一致確認' },
  { id: 'reboot', name: '再起動確認', description: 'CM5再起動と完了通知' },
  { id: 'mqttMessage', name: 'MQTT疎通', description: 'MQTTメッセージ送受信確認' },
];

// targetTypeに応じたテスト項目を取得
const getTestItems = (targetType) => {
  return targetType === 'rauc' ? TEST_ITEMS_RAUC : TEST_ITEMS_OTA_AGENT;
};

// OTAテスト結果ステータス
const TEST_STATUS = {
  PASSED: { label: '成功', color: 'bg-green-100 text-green-700', icon: 'check' },
  COMPLETED: { label: '成功', color: 'bg-green-100 text-green-700', icon: 'check' }, // IoT Jobsからの完了ステータス
  FAILED: { label: '失敗', color: 'bg-red-100 text-red-700', icon: 'x' },
  CHECKING: { label: '確認中', color: 'bg-blue-100 text-blue-700', icon: 'loader' },
  PENDING: { label: '待機中', color: 'bg-slate-100 text-slate-600', icon: 'clock' },
  CREATED: { label: '開始中', color: 'bg-slate-100 text-slate-600', icon: 'clock' }, // ジョブ作成直後
  IN_PROGRESS: { label: '実行中', color: 'bg-blue-100 text-blue-700', icon: 'loader' },
  RUNNING: { label: '実行中', color: 'bg-blue-100 text-blue-700', icon: 'loader' },
  QUEUED: { label: 'キュー待ち', color: 'bg-slate-100 text-slate-600', icon: 'clock' }, // IoT Jobsからのキュー待ち
  CANCELLED: { label: 'キャンセル', color: 'bg-slate-100 text-slate-600', icon: 'x' },
  CANCELED: { label: 'キャンセル', color: 'bg-slate-100 text-slate-600', icon: 'x' }, // IoT Jobsのスペル
  TIMED_OUT: { label: 'タイムアウト', color: 'bg-yellow-100 text-yellow-700', icon: 'alert-circle' },
  UNKNOWN: { label: '不明', color: 'bg-yellow-100 text-yellow-700', icon: 'alert-circle' },
  SKIPPED: { label: 'スキップ', color: 'bg-slate-100 text-slate-500', icon: 'skip-forward' },
};

// GitHub Actions ステップステータスマッピング
const STEP_STATUS = {
  completed: { label: '完了', color: 'bg-green-100 text-green-700', icon: 'check' },
  in_progress: { label: '実行中', color: 'bg-blue-100 text-blue-700', icon: 'loader' },
  queued: { label: '待機中', color: 'bg-slate-100 text-slate-600', icon: 'clock' },
  pending: { label: '待機中', color: 'bg-slate-100 text-slate-600', icon: 'clock' },
  skipped: { label: 'スキップ', color: 'bg-slate-100 text-slate-500', icon: 'minus' },
  failure: { label: '失敗', color: 'bg-red-100 text-red-700', icon: 'x' },
  cancelled: { label: 'キャンセル', color: 'bg-slate-100 text-slate-600', icon: 'x' },
};

export default function HiLSTests() {
  // URLクエリパラメータ
  const [searchParams, setSearchParams] = useSearchParams();
  const queryTestId = searchParams.get('testId');

  // 状態（テスト一覧・詳細）
  const [otaTests, setOtaTests] = useState([]);
  const [selectedOtaTest, setSelectedOtaTest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState(null);

  // 状態（ビルド詳細 - GitHub Actions jobs/steps）
  const [buildDetail, setBuildDetail] = useState(null);
  const [buildDetailLoading, setBuildDetailLoading] = useState(false);

  // 状態（ビルドトリガー）
  const [triggering, setTriggering] = useState(false);

  // 状態（キャンセル中）
  const [cancelling, setCancelling] = useState(false);

  // 状態（テスト実行ダイアログ）
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testType, setTestType] = useState('full'); // 'full' or 'ota-only'
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [showOfflineWarning, setShowOfflineWarning] = useState(false);

  // キャッシュ（メモリ）
  const hilsRunCache = useRef(new Map()); // Map<runId, { data, timestamp }>

  // OTAテスト一覧取得
  const fetchOtaTests = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const result = await buildApi.listHilsTests({ limit: 10, offset: 0 });
      setOtaTests(result.tests || []);
      setHasMore(result.hasMore || false);
      setTotalCount(result.total || 0);
    } catch (err) {
      if (!silent) setError('テスト一覧の取得に失敗しました: ' + err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // もっと読み込む
  const loadMoreTests = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const result = await buildApi.listHilsTests({ limit: 10, offset: otaTests.length });
      setOtaTests((prev) => [...prev, ...(result.tests || [])]);
      setHasMore(result.hasMore || false);
      setTotalCount(result.total || 0);
    } catch (err) {
      console.error('Failed to load more tests:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, otaTests.length]);

  // OTAテスト詳細取得
  const fetchOtaTestDetail = useCallback(async (jobId, test) => {
    try {
      // ビルドタイプの場合は一覧の情報をそのまま使う
      if (test?.type === 'build') {
        setSelectedOtaTest(test);
        return;
      }
      const result = await buildApi.getHilsTest(jobId);
      setSelectedOtaTest(result);
    } catch (err) {
      console.error('Failed to fetch OTA test detail:', err);
      setError('テスト詳細の取得に失敗しました: ' + err.message);
    }
  }, []);

  // ビルド詳細取得（GitHub Actions jobs/steps）- キャッシュ付き
  const fetchBuildDetail = useCallback(async (runId, silent = false) => {
    // キャッシュチェック（完了済みビルドのみキャッシュを使用）
    const cached = hilsRunCache.current.get(runId);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      // 実行中のビルドはキャッシュをスキップ
      const isRunning = cached.data.status === 'in_progress' || cached.data.status === 'queued';
      if (!isRunning) {
        setBuildDetail(cached.data);
        return;
      }
    }

    if (!silent) setBuildDetailLoading(true);
    try {
      const result = await buildApi.getHilsRun(runId);
      setBuildDetail(result);
      // キャッシュに保存
      hilsRunCache.current.set(runId, { data: result, timestamp: Date.now() });
      // 古いキャッシュエントリを削除（最大20件）
      if (hilsRunCache.current.size > 20) {
        const oldestKey = hilsRunCache.current.keys().next().value;
        hilsRunCache.current.delete(oldestKey);
      }
    } catch (err) {
      console.error('Failed to fetch build detail:', err);
      if (!silent) setError('ビルド詳細の取得に失敗しました: ' + err.message);
    } finally {
      if (!silent) setBuildDetailLoading(false);
    }
  }, []);

  // 初期ロード
  useEffect(() => {
    fetchOtaTests();
  }, [fetchOtaTests]);

  // テスト自動選択（クエリパラメータまたは最新を選択）
  useEffect(() => {
    if (otaTests.length > 0 && !selectedOtaTest) {
      if (queryTestId) {
        const targetTest = otaTests.find(t => t.jobId === queryTestId);
        if (targetTest) {
          setSelectedOtaTest(targetTest);
          fetchOtaTestDetail(targetTest.jobId, targetTest);
          // ビルドタイプ or hilsRunIdがあればビルド詳細も取得
          if (targetTest.type === 'build') {
            fetchBuildDetail(targetTest.jobId);
          } else if (targetTest.hilsRunId) {
            fetchBuildDetail(targetTest.hilsRunId);
          }
          setSearchParams({}, { replace: true });
          return;
        }
      }
      // 最新のテストを自動選択
      setSelectedOtaTest(otaTests[0]);
      fetchOtaTestDetail(otaTests[0].jobId, otaTests[0]);
      // ビルドタイプ or hilsRunIdがあればビルド詳細も取得
      if (otaTests[0].type === 'build') {
        fetchBuildDetail(otaTests[0].jobId);
      } else if (otaTests[0].hilsRunId) {
        fetchBuildDetail(otaTests[0].hilsRunId);
      }
    }
  }, [otaTests, selectedOtaTest, fetchOtaTestDetail, fetchBuildDetail, queryTestId, setSearchParams]);

  // 実行中ステータスかどうかをチェックするヘルパー
  const isRunningStatus = (status) =>
    status === 'IN_PROGRESS' || status === 'RUNNING' || status === 'PENDING';

  // 自動更新（実行中テストがあれば5秒間隔で更新）
  useEffect(() => {
    const hasInProgress = otaTests.some((t) => isRunningStatus(t.status));
    if (!hasInProgress) return;

    const interval = setInterval(() => {
      // 一覧を更新
      fetchOtaTests(true);
      // 選択中のテストが実行中ならビルド詳細も更新
      if (selectedOtaTest && isRunningStatus(selectedOtaTest.status)) {
        if (selectedOtaTest.type === 'build') {
          fetchBuildDetail(selectedOtaTest.jobId, true);
        } else if (selectedOtaTest.hilsRunId) {
          fetchBuildDetail(selectedOtaTest.hilsRunId, true);
        }
        fetchOtaTestDetail(selectedOtaTest.jobId, selectedOtaTest);
      }
    }, 10000); // 10秒間隔でリアルタイム更新（パフォーマンス改善）

    return () => clearInterval(interval);
  }, [otaTests, selectedOtaTest, fetchOtaTests, fetchOtaTestDetail, fetchBuildDetail]);

  // 日時フォーマット
  const formatDate = (isoStr) => {
    if (!isoStr) return '-';
    return new Date(isoStr).toLocaleString('ja-JP', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 所要時間のフォーマット
  const formatDuration = (seconds) => {
    if (seconds === undefined || seconds === null) return '';
    if (seconds < 60) return `${seconds}秒`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}分${secs}秒` : `${mins}分`;
  };

  // テスト選択
  const handleSelectTest = (test) => {
    setSelectedOtaTest(test);
    fetchOtaTestDetail(test.jobId, test);
    // ビルドタイプ or hilsRunIdがあればビルド詳細を取得
    if (test.type === 'build') {
      fetchBuildDetail(test.jobId);
    } else if (test.hilsRunId) {
      fetchBuildDetail(test.hilsRunId);
    } else {
      setBuildDetail(null);
    }
  };

  // デバイス一覧を取得
  const fetchDevices = useCallback(async () => {
    setDevicesLoading(true);
    try {
      const result = await buildApi.getHilsDevices();
      setDevices(result.devices || []);
      // 最初のデバイスを選択
      if (result.devices && result.devices.length > 0) {
        setSelectedDevice(result.devices[0].thingName);
      }
    } catch (err) {
      console.error('Failed to fetch devices:', err);
      setError('デバイス一覧の取得に失敗しました: ' + err.message);
    } finally {
      setDevicesLoading(false);
    }
  }, []);

  // テスト実行ダイアログを開く
  const handleOpenTestDialog = () => {
    setShowTestDialog(true);
    setTestType('full');
    setShowOfflineWarning(false);
    fetchDevices();
  };

  // テスト実行ダイアログを閉じる
  const handleCloseTestDialog = () => {
    setShowTestDialog(false);
    setShowOfflineWarning(false);
  };

  // テスト実行を確定
  const handleConfirmTest = async () => {
    // デバイスがオフラインの場合は警告を表示
    const device = devices.find(d => d.thingName === selectedDevice);
    if (device && !device.isOnline && !showOfflineWarning) {
      setShowOfflineWarning(true);
      return;
    }

    // テスト実行
    setTriggering(true);
    setShowTestDialog(false);
    setShowOfflineWarning(false);

    try {
      if (testType === 'ota-only') {
        // OTAテストのみ
        await buildApi.triggerOtaTestOnly({ thingName: selectedDevice });
      } else {
        // フルテスト（HiLSビルド + OTAテスト）
        await buildApi.triggerHilsRun();
      }
      // 現在の選択をクリアして、新しいテストが自動選択されるようにする
      setSelectedOtaTest(null);
      setBuildDetail(null);
      // 少し待ってから一覧を再取得
      setTimeout(() => {
        fetchOtaTests();
      }, 2000);
    } catch (err) {
      console.error('Failed to trigger test:', err);
      setError('テストの開始に失敗しました: ' + err.message);
    } finally {
      setTriggering(false);
    }
  };

  // HiLSビルドをトリガー（旧関数、互換性のため残す）
  const handleTriggerBuild = () => {
    handleOpenTestDialog();
  };

  // ステップのステータスを取得（conclusionを優先）
  const getStepStatus = (step) => {
    if (step.conclusion) {
      return STEP_STATUS[step.conclusion] || STEP_STATUS.completed;
    }
    return STEP_STATUS[step.status] || STEP_STATUS.pending;
  };

  // 実行中のテストがあるかチェック（RUNNING, IN_PROGRESS, PENDING のいずれか）
  const inProgressTest = otaTests.find((t) =>
    t.status === 'IN_PROGRESS' || t.status === 'RUNNING' || t.status === 'PENDING'
  );

  // 実行中のテストを停止
  const handleStopTest = async () => {
    if (cancelling || !inProgressTest) return;
    setCancelling(true);
    try {
      // ビルドタイプはjobIdがrunId、OTAテストはhilsRunIdを使用
      const runIdToCancel = inProgressTest.type === 'build'
        ? inProgressTest.jobId
        : inProgressTest.hilsRunId;

      if (!runIdToCancel) {
        // hilsRunIdがない場合（OTAのみテスト）はIoT Jobをキャンセル
        if (inProgressTest.jobId) {
          await buildApi.cancelHilsOtaTest(inProgressTest.jobId);
        } else {
          throw new Error('キャンセル対象のIDが見つかりません');
        }
      } else {
        await buildApi.cancelHilsRun(runIdToCancel);
      }
      // 一覧とビルド詳細を更新
      setTimeout(() => {
        fetchOtaTests();
        if (selectedOtaTest?.type === 'build') {
          fetchBuildDetail(selectedOtaTest.jobId);
        }
      }, 1000);
    } catch (err) {
      console.error('Failed to stop test:', err);
      setError('停止に失敗しました: ' + err.message);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">HiLSテスト</h1>
        {inProgressTest ? (
          <Button
            onClick={handleStopTest}
            disabled={cancelling}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {cancelling ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                停止中...
              </>
            ) : (
              <>
                <Icon name="square" className="w-4 h-4 mr-2" />
                テスト停止
              </>
            )}
          </Button>
        ) : (
          <Button
            onClick={handleTriggerBuild}
            disabled={triggering}
          >
            {triggering ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                開始中...
              </>
            ) : (
              <>
                <Icon name="play" className="w-4 h-4 mr-2" />
                テスト実行
              </>
            )}
          </Button>
        )}
      </div>

      {error && <Alert variant="error" onClose={() => setError(null)}>{error}</Alert>}

      {/* 左右分割レイアウト */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* 左側: テスト一覧 */}
        <Card padding="p-0" className="lg:col-span-1">
          <div className="p-3 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-slate-900">テスト実行履歴</h3>
              <Button size="sm" variant="ghost" onClick={() => fetchOtaTests()}>
                <Icon name="refresh" className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-1">{otaTests.length}件</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : otaTests.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              テスト結果なし
            </div>
          ) : (
            <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
              {otaTests.map((test) => {
                const testStatus = TEST_STATUS[test.status] || TEST_STATUS.PENDING;
                const isBuild = test.type === 'build';
                return (
                  <div
                    key={test.jobId}
                    className={`p-3 cursor-pointer hover:bg-slate-50 ${
                      selectedOtaTest?.jobId === test.jobId ? 'bg-primary-50' : ''
                    }`}
                    onClick={() => handleSelectTest(test)}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-slate-900">{test.targetType || 'ota-agent'}</span>
                        {isBuild && (
                          <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">ビルド</span>
                        )}
                      </div>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${testStatus.color}`}>
                        <Icon name={testStatus.icon} className="w-3 h-3" />
                        {testStatus.label}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <div className="flex items-center gap-2">
                        {isBuild ? (
                          <>
                            <span className="px-1.5 py-0.5 bg-slate-100 rounded font-mono">#{test.buildNumber}</span>
                            <span className="font-mono truncate max-w-[80px]">{test.branch}</span>
                          </>
                        ) : (
                          <span className="px-1.5 py-0.5 bg-slate-100 rounded font-mono">v{test.version}</span>
                        )}
                      </div>
                      <span>{formatDate(test.triggeredAt || test.createdAt)}</span>
                    </div>
                  </div>
                );
              })}
              {/* もっと見るボタン */}
              {hasMore && (
                <div className="p-3 text-center border-t border-slate-100">
                  <button
                    onClick={loadMoreTests}
                    disabled={loadingMore}
                    className="px-4 py-2 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg disabled:opacity-50"
                  >
                    {loadingMore ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                        読み込み中...
                      </span>
                    ) : (
                      `もっと見る（残り${totalCount - otaTests.length}件）`
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* 右側: テスト詳細 */}
        <Card className="lg:col-span-2">
          {!selectedOtaTest ? (
            <div className="text-center py-16 text-slate-400 border-2 border-dashed border-slate-200 rounded-lg">
              <Icon name="file" className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>テスト履歴からテストを選択してください</p>
            </div>
          ) : selectedOtaTest.type === 'build' ? (
            /* ビルドタイプの詳細表示（GitHub Actions進捗付き） */
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900">ビルド詳細</h3>
                    <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">ビルド</span>
                    {selectedOtaTest.status === 'IN_PROGRESS' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                        実行中
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 font-mono">#{selectedOtaTest.buildNumber}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={() => fetchBuildDetail(selectedOtaTest.jobId)}>
                    <Icon name="refresh" className="w-4 h-4" />
                  </Button>
                  {(buildDetail?.htmlUrl || selectedOtaTest.htmlUrl) && (
                    <a
                      href={buildDetail?.htmlUrl || selectedOtaTest.htmlUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary-600 hover:underline flex items-center gap-1"
                    >
                      <Icon name="external-link" className="w-4 h-4" />
                      GitHub
                    </a>
                  )}
                </div>
              </div>

              {/* ビルド基本情報 */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div>
                  <label className="text-xs text-slate-500">対象</label>
                  <p className="font-medium">{selectedOtaTest.targetType}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500">ステータス</label>
                  <p className={`font-medium ${
                    selectedOtaTest.status === 'PASSED' ? 'text-green-600' :
                    selectedOtaTest.status === 'FAILED' ? 'text-red-600' :
                    selectedOtaTest.status === 'IN_PROGRESS' ? 'text-blue-600' : 'text-slate-600'
                  }`}>
                    {TEST_STATUS[selectedOtaTest.status]?.label || selectedOtaTest.status}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-slate-500">ブランチ</label>
                  <p className="font-mono text-sm">{buildDetail?.branch || selectedOtaTest.branch}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500">コミット</label>
                  <p className="font-mono text-sm truncate">{(buildDetail?.commitSha || selectedOtaTest.commitSha)?.slice(0, 8) || '-'}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500">開始日時</label>
                  <p className="text-sm">{formatDate(buildDetail?.createdAt || selectedOtaTest.triggeredAt)}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500">更新日時</label>
                  <p className="text-sm">{formatDate(buildDetail?.updatedAt || selectedOtaTest.completedAt) || '-'}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500">実行者</label>
                  <p className="text-sm">{buildDetail?.actor || selectedOtaTest.triggeredBy}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500">結果</label>
                  <p className={`text-sm font-medium ${
                    buildDetail?.conclusion === 'success' ? 'text-green-600' :
                    buildDetail?.conclusion === 'failure' ? 'text-red-600' :
                    buildDetail?.conclusion === 'cancelled' ? 'text-slate-500' : 'text-blue-600'
                  }`}>
                    {buildDetail?.conclusion === 'success' ? '成功' :
                     buildDetail?.conclusion === 'failure' ? '失敗' :
                     buildDetail?.conclusion === 'cancelled' ? 'キャンセル' :
                     buildDetail?.status === 'in_progress' ? '実行中' : '-'}
                  </p>
                </div>
              </div>

              {/* GitHub Actions ジョブ進捗 */}
              {buildDetailLoading && !buildDetail ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : buildDetail?.jobs && buildDetail.jobs.length > 0 ? (
                <div className="mb-6">
                  <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                    <Icon name="list" className="w-4 h-4" />
                    ビルドステップ
                    {selectedOtaTest.status === 'IN_PROGRESS' && (
                      <span className="text-xs text-slate-500 font-normal">(10秒ごとに更新)</span>
                    )}
                  </h4>
                  <div className="space-y-3">
                    {buildDetail.jobs.map((job) => (
                      <div key={job.name} className="border border-slate-200 rounded-lg overflow-hidden">
                        {/* ジョブヘッダー */}
                        <div className={`flex items-center justify-between p-3 ${
                          job.conclusion === 'success' ? 'bg-green-50' :
                          job.conclusion === 'failure' ? 'bg-red-50' :
                          job.status === 'in_progress' ? 'bg-blue-50' : 'bg-slate-50'
                        }`}>
                          <div className="flex items-center gap-2">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center ${
                              job.conclusion === 'success' ? 'bg-green-100 text-green-700' :
                              job.conclusion === 'failure' ? 'bg-red-100 text-red-700' :
                              job.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-500'
                            }`}>
                              <Icon name={
                                job.conclusion === 'success' ? 'check' :
                                job.conclusion === 'failure' ? 'x' :
                                job.status === 'in_progress' ? 'loader' : 'clock'
                              } className="w-4 h-4" />
                            </span>
                            <span className="font-medium text-sm">{job.name}</span>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            job.conclusion === 'success' ? 'bg-green-100 text-green-700' :
                            job.conclusion === 'failure' ? 'bg-red-100 text-red-700' :
                            job.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {job.conclusion === 'success' ? '完了' :
                             job.conclusion === 'failure' ? '失敗' :
                             job.conclusion === 'cancelled' ? 'キャンセル' :
                             job.status === 'in_progress' ? '実行中' : '待機中'}
                          </span>
                        </div>

                        {/* ステップ一覧 */}
                        {job.steps && job.steps.length > 0 && (
                          <div className="divide-y divide-slate-100">
                            {job.steps.map((step) => {
                              const stepStatus = getStepStatus(step);
                              const isActive = step.status === 'in_progress';
                              return (
                                <div
                                  key={step.number}
                                  className={`flex items-center justify-between px-3 py-2 text-sm ${
                                    isActive ? 'bg-blue-50' : ''
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${stepStatus.color}`}>
                                      {stepStatus.icon === 'loader' ? (
                                        <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                      ) : (
                                        <Icon name={stepStatus.icon} className="w-3 h-3" />
                                      )}
                                    </span>
                                    <span className={`${isActive ? 'font-medium text-blue-700' : 'text-slate-700'}`}>
                                      {step.name}
                                    </span>
                                  </div>
                                  <span className={`px-1.5 py-0.5 rounded text-xs ${stepStatus.color}`}>
                                    {stepStatus.label}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-slate-50 rounded-lg text-center text-slate-500">
                  <Icon name="loader" className="w-6 h-6 mx-auto mb-2 animate-spin" />
                  <p className="text-sm">ビルド情報を取得中...</p>
                </div>
              )}

              {/* RAUCアーティファクト情報（RAUCモードの場合のみ表示） */}
              {buildDetail?.hilsSession?.otaTest?.raucBundleKey && (
                <div className="mb-6">
                  <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                    <Icon name="package" className="w-4 h-4" />
                    RAUCバンドル
                  </h4>
                  <div className="p-4 rounded-lg border bg-green-50 border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-6 h-6 rounded-full flex items-center justify-center bg-green-100 text-green-700">
                        <Icon name="check" className="w-4 h-4" />
                      </span>
                      <span className="font-medium text-sm">検証済み</span>
                    </div>
                    <div className="text-sm text-slate-600">
                      <span className="text-slate-500">ファイル:</span>
                      <span className="ml-2 font-mono text-xs">{buildDetail.hilsSession.otaTest.raucBundleKey.split('/').pop()}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* OTAテスト結果 */}
              {buildDetail?.hilsSession?.otaTest && (
                <div className="mb-6">
                  <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                    <Icon name="zap" className="w-4 h-4" />
                    OTAテスト
                    <span className="text-slate-500 font-normal">-</span>
                    <span className="font-mono text-sm">{buildDetail.hilsSession.otaTest.targetType || 'ota-agent'}</span>
                    <span className="px-1.5 py-0.5 bg-slate-200 text-slate-700 rounded text-xs font-mono">
                      {buildDetail.hilsSession.otaTest.version}
                    </span>
                    {buildDetail.hilsSession.otaTest.status === 'IN_PROGRESS' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                        実行中
                      </span>
                    )}
                  </h4>

                  {/* テスト項目チェックリスト */}
                  <div className="space-y-2 mb-4">
                    {getTestItems(buildDetail.hilsSession.otaTest.targetType).map((item) => {
                      const result = buildDetail.hilsSession.otaTest.testItems?.find(t => t.id === item.id);
                      const status = TEST_STATUS[result?.status] || TEST_STATUS.PENDING;
                      return (
                        <div
                          key={item.id}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center ${status.color}`}>
                              {status.icon === 'loader' ? (
                                <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <Icon name={status.icon} className="w-4 h-4" />
                              )}
                            </span>
                            <div>
                              <p className="font-medium text-sm">{item.name}</p>
                              <p className="text-xs text-slate-500">{item.description}</p>
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${status.color}`}>
                            {status.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Device Shadow情報 */}
                  {buildDetail.hilsSession.otaTest.deviceShadows && buildDetail.hilsSession.otaTest.deviceShadows.length > 0 && (
                    <div className="p-4 bg-slate-50 rounded-lg mb-4">
                      <h5 className="text-xs font-medium text-slate-500 mb-3">Device Shadow</h5>
                      <div className="space-y-2">
                        {buildDetail.hilsSession.otaTest.deviceShadows.map((shadow, idx) => (
                          <div key={idx} className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="text-slate-400">デバイス:</span>
                              <span className="ml-2 font-mono">{shadow.thingName}</span>
                            </div>
                            {/* RAUCモードの場合は追加フィールドを表示 */}
                            {buildDetail.hilsSession.otaTest.targetType === 'rauc' ? (
                              <>
                                <div>
                                  <span className="text-slate-400">OSバージョン:</span>
                                  <span className="ml-2 font-mono">{shadow.osVersion || '-'}</span>
                                </div>
                                <div>
                                  <span className="text-slate-400">ブートスロット:</span>
                                  <span className="ml-2 font-mono">{shadow.bootSlot || '-'}</span>
                                </div>
                                <div>
                                  <span className="text-slate-400">RAUCステータス:</span>
                                  <span className={`ml-2 font-medium ${
                                    shadow.raucStatus === 'ok' || shadow.raucStatus === 'good'
                                      ? 'text-green-600' : 'text-slate-600'
                                  }`}>
                                    {shadow.raucStatus || '-'}
                                  </span>
                                </div>
                              </>
                            ) : (
                              <>
                                <div>
                                  <span className="text-slate-400">報告バージョン:</span>
                                  <span className="ml-2 font-mono">{shadow.reportedVersion || '-'}</span>
                                </div>
                                <div>
                                  <span className="text-slate-400">期待バージョン:</span>
                                  <span className="ml-2 font-mono">{buildDetail.hilsSession.otaTest.version}</span>
                                </div>
                                <div>
                                  <span className="text-slate-400">一致:</span>
                                  <span className={`ml-2 font-medium ${
                                    shadow.reportedVersion === buildDetail.hilsSession.otaTest.version ? 'text-green-600' : 'text-red-600'
                                  }`}>
                                    {shadow.reportedVersion === buildDetail.hilsSession.otaTest.version ? '一致' : '不一致'}
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* IoT Job実行状況 */}
                  {buildDetail.hilsSession.otaTest.executions && buildDetail.hilsSession.otaTest.executions.length > 0 && (
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h5 className="text-xs font-medium text-slate-500 mb-3">IoT Job 実行状況</h5>
                      <div className="space-y-1">
                        {buildDetail.hilsSession.otaTest.executions.map((exec, idx) => (
                          <div key={idx} className="flex items-center justify-between text-xs">
                            <span className="font-mono">{exec.thingArn?.split('/').pop() || exec.thingName}</span>
                            <span className={`px-1.5 py-0.5 rounded ${
                              exec.status === 'SUCCEEDED' ? 'bg-green-100 text-green-700' :
                              exec.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-700' :
                              exec.status === 'FAILED' ? 'bg-red-100 text-red-700' :
                              exec.status === 'QUEUED' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {exec.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ビルド完了後のOTAテスト案内（hilsSessionがない場合のみ表示） */}
              {selectedOtaTest.status === 'PASSED' && !buildDetail?.hilsSession?.otaAgentBuild && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-700">
                    ビルド完了。OTA配信テストを実行できます。
                  </p>
                </div>
              )}
              {selectedOtaTest.status === 'FAILED' && (
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-700">
                    ビルドが失敗しました。ログを確認してください。
                  </p>
                </div>
              )}
            </>
          ) : (
            /* OTAテストタイプの詳細表示 */
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-slate-900">テスト詳細</h3>
                  <p className="text-sm text-slate-500 font-mono">{selectedOtaTest.jobId}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => fetchOtaTestDetail(selectedOtaTest.jobId, selectedOtaTest)}>
                  <Icon name="refresh" className="w-4 h-4" />
                </Button>
              </div>

              {/* 基本情報 */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="text-xs text-slate-500">対象</label>
                  <p className="font-medium">{selectedOtaTest.targetType || 'ota-agent'}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500">バージョン</label>
                  <p className="font-mono">v{selectedOtaTest.version}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Thing Group</label>
                  <p className="font-mono text-sm">{selectedOtaTest.thingGroup || 'hils-test-devices'}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500">作成日時</label>
                  <p className="text-sm">{formatDate(selectedOtaTest.triggeredAt || selectedOtaTest.createdAt)}</p>
                </div>
              </div>

              {/* HiLSビルド情報（hilsRunIdがある場合のみ表示）- 時系列順で最初に表示 */}
              {selectedOtaTest.hilsRunId && buildDetail && (
                <>
                  {/* GitHub Actions ジョブ進捗 */}
                  {buildDetail.jobs && buildDetail.jobs.length > 0 && (
                    <div className="mb-6 mt-6">
                      <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                        <Icon name="list" className="w-4 h-4" />
                        HiLSビルドステップ
                        {buildDetail.htmlUrl && (
                          <a
                            href={buildDetail.htmlUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary-600 hover:underline flex items-center gap-1"
                          >
                            <Icon name="external-link" className="w-3 h-3" />
                            GitHub
                          </a>
                        )}
                      </h4>
                      <div className="space-y-3">
                        {buildDetail.jobs.map((job) => (
                          <div key={job.name} className="border border-slate-200 rounded-lg overflow-hidden">
                            <div className={`flex items-center justify-between p-3 ${
                              job.conclusion === 'success' ? 'bg-green-50' :
                              job.conclusion === 'failure' ? 'bg-red-50' :
                              job.status === 'in_progress' ? 'bg-blue-50' : 'bg-slate-50'
                            }`}>
                              <div className="flex items-center gap-2">
                                <span className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                  job.conclusion === 'success' ? 'bg-green-100 text-green-700' :
                                  job.conclusion === 'failure' ? 'bg-red-100 text-red-700' :
                                  job.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                  'bg-slate-100 text-slate-500'
                                }`}>
                                  <Icon name={
                                    job.conclusion === 'success' ? 'check' :
                                    job.conclusion === 'failure' ? 'x' :
                                    job.status === 'in_progress' ? 'loader' : 'clock'
                                  } className="w-4 h-4" />
                                </span>
                                <span className="font-medium text-sm">{job.name}</span>
                              </div>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                job.conclusion === 'success' ? 'bg-green-100 text-green-700' :
                                job.conclusion === 'failure' ? 'bg-red-100 text-red-700' :
                                job.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                'bg-slate-100 text-slate-600'
                              }`}>
                                {job.conclusion === 'success' ? '完了' :
                                 job.conclusion === 'failure' ? '失敗' :
                                 job.conclusion === 'cancelled' ? 'キャンセル' :
                                 job.status === 'in_progress' ? '実行中' : '待機中'}
                              </span>
                            </div>
                            {job.steps && job.steps.length > 0 && (
                              <div className="divide-y divide-slate-100">
                                {job.steps.map((step) => {
                                  const stepStatus = getStepStatus(step);
                                  return (
                                    <div key={step.number} className="flex items-center justify-between px-3 py-2 text-sm">
                                      <div className="flex items-center gap-2">
                                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${stepStatus.color}`}>
                                          <Icon name={stepStatus.icon} className="w-3 h-3" />
                                        </span>
                                        <span className="text-slate-700">{step.name}</span>
                                      </div>
                                      <span className={`text-xs ${stepStatus.color}`}>{stepStatus.label}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* RAUCバンドル情報（RAUCモードの場合） */}
                  {buildDetail.hilsSession?.otaTest?.raucBundleKey && (
                    <div className="mb-6">
                      <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                        <Icon name="package" className="w-4 h-4" />
                        RAUCバンドル
                      </h4>
                      <div className="p-4 rounded-lg border bg-green-50 border-green-200">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                            <Icon name="check" className="w-3 h-3 inline mr-1" />
                            検証済み
                          </span>
                        </div>
                        <div className="text-xs text-slate-600">
                          <div className="flex items-center gap-1">
                            <Icon name="folder" className="w-3 h-3" />
                            <span className="font-mono truncate">{buildDetail.hilsSession.otaTest.raucBundleKey.split('/').pop()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* 試験項目チェックリスト - OTAテスト結果 */}
              <div className="mb-6">
                <h4 className="font-medium text-slate-900 mb-3">試験項目</h4>
                <div className="space-y-2">
                  {getTestItems(selectedOtaTest.targetType).map((item) => {
                    const result = selectedOtaTest.testItems?.find(t => t.id === item.id);
                    const status = TEST_STATUS[result?.status] || TEST_STATUS.PENDING;
                    return (
                      <div
                        key={item.id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center ${status.color}`}>
                            <Icon name={status.icon} className="w-4 h-4" />
                          </span>
                          <div>
                            <p className="font-medium text-sm">{item.name}</p>
                            <p className="text-xs text-slate-500">{item.description}</p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${status.color}`}>
                          {status.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Device Shadow情報 */}
              {selectedOtaTest.deviceShadow && (
                <div className="p-4 bg-slate-50 rounded-lg mb-4">
                  <h4 className="text-xs font-medium text-slate-500 mb-3">Device Shadow</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-slate-400">デバイス:</span>
                      <span className="ml-2 font-mono">{selectedOtaTest.deviceShadow.thingName}</span>
                    </div>
                    {/* RAUCモードの場合はOSバージョンを表示 */}
                    {selectedOtaTest.targetType === 'rauc' ? (
                      <>
                        <div>
                          <span className="text-slate-400">OSバージョン:</span>
                          <span className="ml-2 font-mono">{selectedOtaTest.deviceShadow.osVersion || '-'}</span>
                        </div>
                        <div>
                          <span className="text-slate-400">ブートスロット:</span>
                          <span className="ml-2 font-mono">{selectedOtaTest.deviceShadow.bootSlot || '-'}</span>
                        </div>
                        <div>
                          <span className="text-slate-400">RAUCステータス:</span>
                          <span className={`ml-2 font-medium ${
                            selectedOtaTest.deviceShadow.raucStatus === 'ok' || selectedOtaTest.deviceShadow.raucStatus === 'good'
                              ? 'text-green-600' : 'text-slate-600'
                          }`}>
                            {selectedOtaTest.deviceShadow.raucStatus || '-'}
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <span className="text-slate-400">報告バージョン:</span>
                          <span className="ml-2 font-mono">{selectedOtaTest.deviceShadow.reportedVersion || '-'}</span>
                        </div>
                        <div>
                          <span className="text-slate-400">期待バージョン:</span>
                          <span className="ml-2 font-mono">{selectedOtaTest.deviceShadow.expectedVersion}</span>
                        </div>
                        <div>
                          <span className="text-slate-400">一致:</span>
                          <span className={`ml-2 font-medium ${selectedOtaTest.deviceShadow.versionMatch ? 'text-green-600' : 'text-red-600'}`}>
                            {selectedOtaTest.deviceShadow.versionMatch ? '一致' : '不一致'}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* IoT Jobステータス */}
              {selectedOtaTest.jobStatus && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="text-xs font-medium text-slate-500 mb-3">IoT Job ステータス</h4>
                  <div className="mb-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      selectedOtaTest.jobStatus.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                      selectedOtaTest.jobStatus.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-700' :
                      selectedOtaTest.jobStatus.status === 'FAILED' ? 'bg-red-100 text-red-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {selectedOtaTest.jobStatus.status}
                    </span>
                  </div>
                  {selectedOtaTest.jobStatus.executions && selectedOtaTest.jobStatus.executions.length > 0 && (
                    <div className="space-y-1">
                      {selectedOtaTest.jobStatus.executions.map((exec, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs">
                          <span className="font-mono">{exec.thingName}</span>
                          <span className={`px-1.5 py-0.5 rounded ${
                            exec.status === 'SUCCEEDED' ? 'bg-green-100 text-green-700' :
                            exec.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-700' :
                            exec.status === 'FAILED' ? 'bg-red-100 text-red-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {exec.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {/* テスト実行ダイアログ */}
      {showTestDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">テスト実行</h3>
            </div>

            <div className="p-4 space-y-4">
              {/* テストタイプ選択 */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">テストタイプ</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
                    <input
                      type="radio"
                      name="testType"
                      value="full"
                      checked={testType === 'full'}
                      onChange={(e) => setTestType(e.target.value)}
                      className="w-4 h-4 text-primary-600"
                    />
                    <div>
                      <p className="font-medium text-slate-900">フルテスト</p>
                      <p className="text-xs text-slate-500">HiLSビルド（RAUC） + OTAテスト</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
                    <input
                      type="radio"
                      name="testType"
                      value="ota-only"
                      checked={testType === 'ota-only'}
                      onChange={(e) => setTestType(e.target.value)}
                      className="w-4 h-4 text-primary-600"
                    />
                    <div>
                      <p className="font-medium text-slate-900">OTAテストのみ</p>
                      <p className="text-xs text-slate-500">既存のRAUCバンドルでOTAテスト（ビルドスキップ）</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* デバイス選択 */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">対象デバイス</label>
                {devicesLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                    <span className="ml-2 text-sm text-slate-500">読み込み中...</span>
                  </div>
                ) : devices.length === 0 ? (
                  <p className="text-sm text-slate-500 py-2">デバイスが見つかりません</p>
                ) : (
                  <div className="space-y-2">
                    {devices.map((device) => (
                      <label
                        key={device.thingName}
                        className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-slate-50 ${
                          selectedDevice === device.thingName ? 'border-primary-500 bg-primary-50' : 'border-slate-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="device"
                            value={device.thingName}
                            checked={selectedDevice === device.thingName}
                            onChange={(e) => setSelectedDevice(e.target.value)}
                            className="w-4 h-4 text-primary-600"
                          />
                          <div>
                            <p className="font-mono text-sm text-slate-900">{device.thingName}</p>
                            <p className="text-xs text-slate-500">v{device.version}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${device.isOnline ? 'bg-green-500' : 'bg-red-500'}`} />
                          <span className={`text-xs ${device.isOnline ? 'text-green-600' : 'text-red-600'}`}>
                            {device.isOnline ? 'オンライン' : 'オフライン'}
                          </span>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* オフライン警告 */}
              {showOfflineWarning && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Icon name="alert-triangle" className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">デバイスがオフラインです</p>
                      <p className="text-xs text-yellow-700 mt-1">
                        選択したデバイスはネットワークに接続されていません。
                        テストを実行してもOTAジョブはデバイスに配信されない可能性があります。
                      </p>
                      <p className="text-xs text-yellow-700 mt-1">続行しますか？</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
              <Button variant="ghost" onClick={handleCloseTestDialog}>
                キャンセル
              </Button>
              <Button
                onClick={handleConfirmTest}
                disabled={triggering || devicesLoading || devices.length === 0}
              >
                {triggering ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    実行中...
                  </>
                ) : showOfflineWarning ? (
                  '続行する'
                ) : (
                  'テスト実行'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
